import React from 'react';
import { useTranslation } from 'react-i18next';
import CgTableSkeleton from 'modules/Common/table/CgTableSkeleton';
import { formatDateUTC, DATE_FORMAT } from 'helpers/dateParser';
import { numberFormatter } from 'helpers/intl';
import usePlanSummary from 'modules/App/hooks/usePlanSummary';
import { expiryTone, EXPIRED_TONE } from './expiryTone';

// Format a plan price in the currency the API reports for this organization.
//
// With no currency the value renders as a bare grouped number rather than
// guessing a symbol — the same rule the import flows follow, and for the same
// reason: a wrong symbol is a worse answer than no symbol. Fraction digits are
// 0-2 so a round 2500 reads as "€2,500" while 499.99 keeps its pence.
const formatPrice = (price, currency) =>
  numberFormatter(undefined, {
    ...(currency ? { style: 'currency', currency } : {}),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(price);

// Panel rendered inside the Clerk OrganizationProfile dialog, as a sibling of
// the Info/Preferences/Roles pages. The content column is ~640px wide, so the
// type scale below matches OrgProfileRolesPanel rather than a page-level header.
//
// The panel answers ONE question — when does this plan run out — so the expiry
// date is the only headline. Everything else (plan name, start date, a separate
// status row) was tabular noise around that one fact and has been removed; the
// active/expired state now reads as a badge beside the date.
//
// Read-only by design: the backend has no cancel/downgrade/renew endpoint, so
// there is nothing here to write. The plan comes from GET /plan/summary — the
// database is the plan authority, and Clerk's publicMetadata.plan carries no
// dates.

const Badge = ({ tone, children }) => (
  // px/py as arbitrary values: Bootstrap's spacing utilities are !important and
  // would outrank Tailwind's own p-* here (see the styling notes in CLAUDE.md).
  <span
    className={`inline-flex shrink-0 items-center rounded-full px-[10px] py-[2px] text-xs font-semibold ${tone}`}
  >
    {children}
  </span>
);

export default function OrgProfilePlanPanel() {
  const { t } = useTranslation('app');
  const { data, isLoading, error } = usePlanSummary();

  const heading = <h1>{t('orgProfile.plan.title', 'Plan')}</h1>;

  const shell = (children) => (
    <div className="flex flex-col gap-2 mt-1">
      {heading}
      {children}
    </div>
  );

  if (isLoading) {
    return shell(
      <CgTableSkeleton
        rows={2}
        columns={1}
        showHeader={false}
        showPagination={false}
      />,
    );
  }

  if (error) {
    return shell(
      <p className="text-sm text-slate-500 m-0">
        {t('orgProfile.plan.loadError', 'The plan could not be loaded. Please try again later.')}
      </p>,
    );
  }

  // No plan context at all: a consultant org (which does not render this tab),
  // or an operator/declarant with no assignment yet. Not an error state.
  if (!data || data.status === 'none' || !data.plan) {
    return shell(
      <p className="text-sm text-slate-500 m-0">
        {t('orgProfile.plan.none', 'This organization does not have a plan assigned.')}
      </p>,
    );
  }

  const { status, expiresAt, isPermanent, daysRemaining, price, currency } = data;
  const isExpired = status === 'expired';
  const tone = isExpired ? EXPIRED_TONE : expiryTone(daysRemaining);

  // A permanent plan must never read as expiring today, so both the headline and
  // the countdown are driven by isPermanent rather than by a null date.
  const headline = isPermanent
    ? t('orgProfile.plan.neverExpires', 'Does not expire')
    : formatDateUTC(expiresAt, DATE_FORMAT.LONG);

  const caption = isExpired
    ? t('orgProfile.plan.expiredOnLabel', 'Expired on')
    : t('orgProfile.plan.expiresOnLabel', 'Expires on');

  return shell(
    // White ground, not a tinted one: urgency is carried by the countdown's
    // colour alone, so the card stays quiet until there is something to say.
    <div className="bg-white border border-slate-200 rounded-lg p-[20px]">
      {/* Two columns of equal weight — expiry and price answer the two questions
          people open this tab for, so neither is subordinate. `min-w-0` keeps a
          long localized date from forcing the row wider than the dialog.
          The badge rides in the same row, aligned to the caption line, so it
          costs no vertical space of its own. */}
      <div className="flex items-start gap-[24px]">
        <div className="flex-1 min-w-0">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {isPermanent ? t('orgProfile.plan.renewal', 'Renewal') : caption}
          </span>
          <p className="text-[28px] leading-tight font-semibold text-slate-900 mt-1 mb-0">
            {headline}
          </p>
          {!isPermanent && (
            // The only coloured element on the card: blue > 7 days, amber ≤ 7,
            // red ≤ 3, red once expired.
            <p className={`text-sm font-medium ${tone.text} mt-1 mb-0`}>
              {isExpired
                ? t('orgProfile.plan.expiredAgo', 'This plan is no longer active.')
                : t('orgProfile.plan.daysRemaining', {
                    count: daysRemaining ?? 0,
                    defaultValue: '{{count}} day remaining',
                    defaultValue_other: '{{count}} days remaining',
                  })}
            </p>
          )}
        </div>

        {/* Only when a price is actually recorded. `price == null` is "not
            recorded" and must not read as free; a recorded 0 still shows. With
            no price the expiry column simply takes the full width rather than
            leaving half the card empty. */}
        {price != null && (
          // shrink-0 with natural width, NOT flex-1: an equal split gave the
          // price as much room as the date and pushed "8 September 2026" onto a
          // second line. A price is short and predictable, a localized date is
          // not, so the price takes what it needs and the date column keeps the
          // slack.
          <div className="shrink-0 border-l border-slate-200 pl-[24px]">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t('orgProfile.plan.price', 'Price')}
            </span>
            <p className="text-[28px] leading-tight font-semibold text-slate-900 mt-1 mb-0">
              {formatPrice(price, currency)}
            </p>
          </div>
        )}

        {/* shrink-0 so the badge never compresses; the columns absorb the width. */}
        <Badge tone={isExpired ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}>
          {isExpired
            ? t('orgProfile.plan.statusExpired', 'Expired')
            : t('orgProfile.plan.statusActive', 'Active')}
        </Badge>
      </div>
    </div>,
  );
}
